/**
 * 89. Roles & Permissions — role editor; permission matrix; data scope;
 * field-level permissions; SOD conflict warnings.
 *
 * Arabic and English (C7): every word from the catalogs. A system role
 * (`isSystem`) is named by its code in the interface's language (the
 * product's words, seeded in English); a role the workspace made keeps the
 * name it was given.
 */
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatQuantity } from "@/lib/money";

interface Role {
  id: string;
  name: string;
  code: string;
  description: string | null;
  isSystem: boolean;
  status: "Draft" | "Active" | "Superseded";
  permissionCount: number;
  memberCount: number;
  sodConflicts: number;
}

export function RolesPermissionsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<Role>(
    ["admin", "roles"],
    "/admin/roles",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.roles.title")}
        subtitle={t("admin.roles.subtitle")}
        actions={
          <PendingButton variant="primary" reason="people" slice="RR-W1">
            {t("admin.roles.create")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("admin.roles.list"), data.total)}>
          <DataTable<Role>
            rows={data.items}
            columns={[
              {
                key: "name",
                label: t("admin.roles.role"),
                render: (r) =>
                  r.isSystem
                    ? t(`values.systemRole.${r.code}`, { defaultValue: r.name })
                    : r.name,
              },
              {
                key: "code",
                label: t("fields.code"),
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              {
                key: "isSystem",
                label: t("fields.type"),
                render: (r) =>
                  r.isSystem ? (
                    <Badge tone="primary">{t("admin.roles.system")}</Badge>
                  ) : (
                    <Badge tone="default">{t("admin.roles.custom")}</Badge>
                  ),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => <StatusBadge status={r.status} type="Role" />,
              },
              {
                key: "permissionCount",
                label: t("admin.roles.permissions"),
                numeric: true,
                render: (r) => formatQuantity(r.permissionCount, 0),
              },
              {
                key: "memberCount",
                label: t("admin.roles.members"),
                numeric: true,
                render: (r) => formatQuantity(r.memberCount, 0),
              },
              {
                key: "sodConflicts",
                label: t("admin.roles.sodConflicts"),
                numeric: true,
                render: (r) =>
                  r.sodConflicts > 0 ? (
                    <Badge tone="danger">
                      {formatQuantity(r.sodConflicts, 0)}
                    </Badge>
                  ) : (
                    <Badge tone="success">{formatQuantity(0, 0)}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
